import { Close, TableSplit, View, WarningAltFilled } from '@carbon/icons-react';
import * as React from 'react';
import {
  Body,
  Button,
  Heading,
  Icon,
  IconComponentType,
  Tooltip,
} from 'vet-bones/design-system/components';

import { DataSourceIcon } from 'src/components/resourceDialog/DataSourceIcon';
import {
  getVgResourceCardRemoveButtonTestId,
  getVgResourceCardTestId,
  getVgResourceCardViewPreviewButtonTestId,
} from 'src/constants/testIds';
import { VgTableMetadata } from 'src/containers/ProjectNewResourceDialog/hooks/useVirtualGraphDialogState';
import { currentFeatureVersionSelector } from 'src/selectors/currentFeatureVersion';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

import styles from 'src/containers/ProjectNewResourceDialog/components/projectResourceCard.module.scss';

export type VirtualGraphResourceCardProps = {
  name: string;
  resourceIcon?: IconComponentType; // Optional, for backward compatibility
  dataSourceType?: string; // Data source type for icon
  metadata?: VgTableMetadata;
  validationError?: string;
  onViewPreview: () => void;
  onRemove: () => void;
};

export const VirtualGraphResourceCard: React.VFC<
  VirtualGraphResourceCardProps
> = ({
  name,
  resourceIcon,
  dataSourceType,
  metadata,
  validationError,
  onViewPreview,
  onRemove,
}) => {
  const stardogVersion = useReduxSelector(currentFeatureVersionSelector);

  const description = metadata
    ? copy.components.projectResourceDialog.resourceTypePanel.vgResourceCard.description(
        metadata.fields.length,
        metadata.rowCount ?? 0,
        metadata.fields.map((f) => f.label).join(', ')
      )
    : null;

  let icon: JSX.Element;
  if (dataSourceType) {
    icon = (
      <DataSourceIcon
        dataSourceType={dataSourceType}
        size={24}
        stardogVersion={stardogVersion}
      />
    );
  } else if (resourceIcon) {
    icon = <Icon IconComponent={resourceIcon} size="md" />;
  } else {
    icon = <Icon IconComponent={TableSplit} size="md" />;
  }

  return (
    <div
      className={styles.projectResourceCard}
      data-testid={getVgResourceCardTestId(name)}
    >
      <div className={styles.header}>
        <div className={styles.icon}>{icon}</div>
        <div className={styles.content}>
          <Heading size="sm">{name}</Heading>
        </div>
        <div className={styles.actions}>
          {validationError ? (
            <Tooltip description={validationError}>
              <div
                className={styles.errorIcon}
                data-testid="vg-resource-card-error-icon"
              >
                <Icon IconComponent={WarningAltFilled} size="md" />
              </div>
            </Tooltip>
          ) : null}
          <Tooltip
            description={
              copy.components.projectResourceDialog.resourceTypePanel
                .projectResourceCard.viewPreview
            }
          >
            <Button
              icon={View}
              onClick={onViewPreview}
              size="xs"
              testId={getVgResourceCardViewPreviewButtonTestId(name)}
              variant="secondary-transparent-gray"
            />
          </Tooltip>
          <Tooltip
            description={
              copy.components.projectResourceDialog.resourceTypePanel
                .vgResourceCard.removeTable
            }
          >
            <Button
              icon={Close}
              onClick={onRemove}
              size="xs"
              testId={getVgResourceCardRemoveButtonTestId(name)}
              variant="secondary-transparent-gray"
            />
          </Tooltip>
        </div>
      </div>
      {description ? (
        <Tooltip description={description}>
          <Body className={styles.description} size="md">
            {description}
          </Body>
        </Tooltip>
      ) : null}
    </div>
  );
};
